<template>
	<v-card
		variant="outlined"
		width="100%"
		data-test="usage-detail-card"
	>
		<div
			v-if="showHeader"
			class="card-header"
		>
			<slot name="header">
				<div
					v-if="iconSrc"
					class="detail-card-icon sgnl-hidden sgnl-sm-block sgnl-flex-shrink-0"
					:style="iconMaskStyle"
				/>
				<v-icon
					v-else-if="icon"
					:size="iconSize"
					:color="iconColor"
					class="sgnl-hidden sgnl-sm-flex sgnl-flex-shrink-0"
					>{{ icon }}</v-icon
				>
				<div class="card-header__info">
					<div class="sgnl-paragraph-m-semibold sgnl-text-color-default">
						{{ title }}
					</div>
					<div
						v-if="subtitleToDisplay"
						class="sgnl-paragraph-s-regular line-clamp-1 sgnl-text-color-secondary"
					>
						{{ subtitleToDisplay }}
					</div>
				</div>
				<span
					v-if="showUpgradeButton"
					class="sgnl-flex-shrink-0"
				>
					<UpgradeButton
						btn-text="Upgrade"
						size="default"
					/>
				</span>
			</slot>
		</div>
		<div
			v-if="showHeader"
			class="section-divider"
		/>
		<div class="usage-quotas-list">
			<div
				class="usage-detail-card-row"
				v-for="(quota, i) in allowedQuotas"
				:key="i"
			>
				<div class="row-header">
					<div class="sgnl-paragraph-s-medium sgnl-text-color-default">
						{{ quota.name }}
					</div>
					<div class="sgnl-paragraph-s-medium sgnl-text-color-default">
						<SkeletonBlock
							v-if="allowanceUnresolved(quota.limit)"
							width="76px"
						/>
						<template v-else>
							<span :data-test="`quota-usage-${quota.id}`"
								>{{ toStringWithSIMagnitude(quota.usage) }}{{ quota.unit }}</span
							>
							<span class="sgnl-paragraph-s-regular sgnl-text-color-secondary"> of </span>
							<span
								v-if="quota.limit === undefined"
								:data-test="`quota-limit-${quota.id}`"
								>unlimited</span
							>
							<span
								v-else
								:data-test="`quota-limit-${quota.id}`"
								>{{ toStringWithSIMagnitude(quota.limit) }}{{ quota.unit }}</span
							>
							{{ suffix(quota.quotaSuffix, quota.usage, quota.limit) }}
						</template>
					</div>
				</div>
				<v-progress-linear
					:model-value="usagePercentage(quota.usage, quota.limit)"
					:color="usageBarColour(quota.usage, quota.limit)"
					height="7"
				/>
			</div>
		</div>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";
import UpgradeButton from "@/components/Common/UpgradeButton.vue";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { toStringWithSIMagnitude } from "@/js/utilities";
import { TierLimitDisplayObject } from "@/js/tierconfig";

export default defineComponent({
	name: "UsageDetailCard",
	components: { UpgradeButton, SkeletonBlock },
	props: {
		title: { type: String, default: "" },
		subtitle: {
			type: Object,
			default: () => ({
				default: "",
				warning: "",
				error: "",
			}),
		},
		icon: { type: String },
		iconSrc: { type: String },
		iconSize: { type: Number, default: 36 },
		usageQuotas: { type: Array as PropType<TierLimitDisplayObject[]>, default: () => [] },
		warningThreshold: { type: Number, default: 80 },
		colourful: { type: Boolean, default: false },
		showHeader: { type: Boolean, default: true },
		tierResolved: { type: Boolean, default: false },
	},
	computed: {
		allowedQuotas(): TierLimitDisplayObject[] {
			return this.usageQuotas.filter((v) => v.limit !== null);
		},
		someQuotasOverThreshold(): boolean {
			return this.usageQuotas.some((v) => {
				return this.usagePercentage(v.usage, v.limit) > this.warningThreshold;
			});
		},
		noResourcesInClass(): boolean {
			return this.usageQuotas.every((v) => {
				return v.limit === null;
			});
		},

		iconColor(): string {
			return this.someQuotasOverThreshold ? "warning" : "primary";
		},
		iconMaskStyle(): Record<string, string> {
			const url = `url("${this.iconSrc}")`;
			return {
				width: `${this.iconSize}px`,
				height: `${this.iconSize}px`,
				"-webkit-mask-image": url,
				"mask-image": url,
			};
		},
		subtitleToDisplay(): string {
			if (this.noResourcesInClass) {
				return this.subtitle?.error ?? "Upgrade to access more features.";
			} else if (this.someQuotasOverThreshold) {
				return this.subtitle.warning;
			} else {
				return this.subtitle.default;
			}
		},
		...mapState(useUserStore, {
			currentUserTierCode: "currentUserTierCode",
		}),
		showUpgradeButton(): boolean {
			const noTierToUpgradeTo = this.currentUserTierCode == "enterprise-tier-l";
			if (noTierToUpgradeTo) {
				return false;
			}
			return this.noResourcesInClass || this.someQuotasOverThreshold;
		},
	},
	methods: {
		allowanceUnresolved(limit: number | null | undefined): boolean {
			return limit === 0 && !this.tierResolved;
		},
		toStringWithSIMagnitude(value) {
			return toStringWithSIMagnitude(value);
		},
		usageBarColour(usage, limit) {
			if (limit === undefined || limit === null || !this.validNumericalLimit(usage, limit)) {
				return "grey";
			}
			const usagePercentage = this.usagePercentage(usage, limit);
			if (usagePercentage >= 100) {
				return "danger";
			} else if (usagePercentage >= this.warningThreshold) {
				return "warning";
			}
			return "var(--sgnl-brand-background)";
		},
		usagePercentage(usage, limit) {
			if (!this.validNumericalLimit(usage, limit)) return 0;
			return (100 * usage) / limit;
		},
		validNumericalLimit(usage, limit) {
			return !(limit === undefined || limit === null || limit === 0 || isNaN(usage) || isNaN(limit));
		},
		suffix(quotaSuffix, usage, limit) {
			if (!quotaSuffix || quotaSuffix.length == 0) {
				return;
			}

			const isSingular = this.validNumericalLimit(usage, limit) ? limit == 1 : usage == 1;

			if (typeof quotaSuffix == "string") {
				return isSingular ? quotaSuffix : quotaSuffix + "s";
			} else if (Array.isArray(quotaSuffix)) {
				if (quotaSuffix.length > 1) {
					return isSingular ? quotaSuffix[0] : quotaSuffix[1];
				} else {
					console.warn("unexpected suffix structure");
					return isSingular ? quotaSuffix[0] : quotaSuffix[0] + "s";
				}
			}
			console.warn("unexpected suffix type");
			return;
		},
	},
});
</script>

<style lang="scss" scoped>
.detail-card-icon {
	flex-shrink: 0;
	background-color: var(--sgnl-default-tertiary-icon);
	-webkit-mask-size: contain;
	mask-size: contain;
	-webkit-mask-repeat: no-repeat;
	mask-repeat: no-repeat;
	-webkit-mask-position: center;
	mask-position: center;
}
.usage-quotas-list {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
	padding: var(--sgnl-space-16);
	/* Preserve the former last-row sgnl-mb-12 below the final row. */
	padding-bottom: calc(var(--sgnl-space-16) + var(--sgnl-space-12));
}
@media (min-width: 600px) {
	.usage-quotas-list {
		gap: var(--sgnl-space-8);
		padding-bottom: calc(var(--sgnl-space-16) + var(--sgnl-space-8));
	}
}
.usage-detail-card-row {
	display: flex;
	flex-direction: column;
	gap: 0.125rem;
}
.usage-detail-card-row .row-header {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
}
</style>
